/* Dependencies */
import anime from 'animejs';

// Models
import { AccordionProps } from './Accordion.model';

/**
 * Accordion
 * @class
 */
class Accordion {
  public parentElement: HTMLElement;
  public triggerElement: HTMLElement;
  public contentElement: HTMLElement;
  public contentContainer: HTMLElement;
  private isOpen: boolean = false;

  constructor(props: AccordionProps) {
    this.parentElement = props.parentElement;
    this.triggerElement = props.triggerElement;
    this.contentContainer = props.contentContainer;
    this.contentElement = props.contentElement;

    // Open by default
    if (props.defaultOpen) {
      this.open(true);
    }

    // Attach events
    this.bindEvents();
  }

  /**
   * Opens the accordion
   */
  open(preventAnimation?: boolean): void {
    // Safeguard against double-clicks.
    if (this.isOpen) return;

    this.isOpen = true;

    // Handles onload open to prevent animation.
    if (preventAnimation) {
      this.contentElement.style.visibility = 'visible';
      this.contentContainer.style.maxHeight = 'none';
      this.triggerElement.setAttribute('aria-expanded', 'true');
      this.parentElement.setAttribute('data-active', 'true');
      return;
    }

    this.contentElement.style.visibility = 'visible';

    anime({
      targets: this.contentContainer,
      maxHeight: [0, this.contentElement.clientHeight + 'px'],
      duration: 200,
      easing: 'linear',
      complete: () => {
        this.contentContainer.style.maxHeight = 'none';
        this.contentElement.style.visibility = 'visible';
        this.triggerElement.setAttribute('aria-expanded', 'true');
        this.parentElement.setAttribute('data-active', 'true');
      },
    });
  }

  /**
   * Closes the accordion
   */
  close(): void {
    // Safeguard against double-clicks.
    if (!this.isOpen) return;

    this.isOpen = false;

    anime({
      targets: this.contentContainer,
      maxHeight: [this.contentElement.clientHeight + 'px', 0],
      duration: 200,
      easing: 'linear',
      complete: () => {
        this.contentContainer.style.maxHeight = '0';
        this.contentElement.style.visibility = 'hidden';
        this.triggerElement.setAttribute('aria-expanded', 'false');
        this.parentElement.setAttribute('data-active', 'false');
      },
    });
  }

  /**
   * Toggles the accordion open / closed.
   * @param event - The input event.
   */
  toggle(event: Event): void {
    if (event) {
      event.preventDefault();
    }

    !this.isOpen ? this.open() : this.close();
  }

  /**
   * Binds the related events for the accordion.
   */
  bindEvents(): void {
    this.triggerElement.addEventListener('click', this.toggle.bind(this));
  }
}

/**
 * Initiates the accordion functionality.
 * @returns
 */
export const accordionInit = () => {
  // Detect if elements are present.
  if (!document.querySelector('[data-accordion]')) return;

  // Get all accordion elements.
  const accordionElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-accordion-item]')
  );

  // Loop and create accordion instances.
  accordionElements.forEach((accordionElement) => {
    // Retrieve additional elements
    const trigger = accordionElement.querySelector(
      '[data-accordion-trigger]'
    ) as HTMLElement;
    const contentContainer = accordionElement.querySelector(
      '[data-accordion-content-container]'
    ) as HTMLElement;
    const content = accordionElement.querySelector(
      '[data-accordion-content]'
    ) as HTMLElement;

    // Create accordion.
    new Accordion({
      parentElement: accordionElement,
      triggerElement: trigger,
      contentElement: content,
      contentContainer: contentContainer,
      defaultOpen: accordionElement.getAttribute('data-default-open') !== null,
    });
  });
};
